import './DimQuantitativeObjectiveEditForm.css';

import React, { useEffect, useState } from 'react';
import { useLoginContext } from '../../../../App';
import { useContentContext } from '../../../layout/Layout';
import { isIndicatorDimensional } from '../helper';
import { generateSelectField } from '../../../b2b-common/helpers';
import B2BFormContainer from '../../../b2b-common/B2BFormContainer';
import GenericTabs from '../../../b2b-common/GenericTabs';
import DatesRangeEditor from '../dates/DatesRangeEditor';
import EditText from '../../../b2b-common/EditText';
import CustomButton from '../../../b2b-common/CustomButton';
import { Add, Cancel, Remove, Save } from '@mui/icons-material';
import DimensionalValuesTable from './DimensionalValuesTable';
import Applicability from './Applicability';
import Tolerance from './Tolerance';
import { hasMainDimObjectiveChanged, getDimensionChanges, createRegularObjectiveFromDimensionalObjective, createRegularObjectivesFromDimensionalObjective, getDimensionalQuantitativeObjectiveTypes, getDimObjectiveType, renderSVGImage } from './helper';
import { saveNewBulkOfObjectives, deleteObjective, updateObjective } from '../../../../services/b2b-plans-apiService';
import { showNotification } from '../../../b2b-common/Notification';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';

const DimQuantitativeObjectiveEditForm = ({
    dimensionalObjective = null,
    planUUID, areaId, areaIndicatorId,
    indicator = null,
    startDate, endDate,
    disabledDateRanges = [],
    onSave, onDelete, onCancel }) => {

    const initialDimensionalValue = {
        "objectiveId": 0,
        "quantitativeDimensionOrder": 0,
        "startQuantitativeValue": 0,
        "endQuantitativeValue": 0,
    }

    const initialDimensionalObjective = {
        "objectiveType": 'QUANTITATIVE_DIMENSIONAL',
        "startDate": startDate || null,
        "endDate": endDate || null,
        "applicability": null,
        "valuesPerDimension": [],
    }

    const { t } = useB2BTranslation('b2b-plans');
    const { loginData } = useLoginContext();
    const { setNotification } = useContentContext();
    const [error, setError] = useState(null);

    const [currentObjective, setCurrentObjective] = useState(dimensionalObjective ?
        { ...dimensionalObjective, startDate: startDate || dimensionalObjective.startDate, endDate: endDate || dimensionalObjective.endDate } : initialDimensionalObjective);
    const [selectedObjectiveType, setSelectedObjectiveType] = useState(getDimObjectiveType(currentObjective));
    const [operation, setOperation] = useState(null);
    const [currentDimensionalType, setCurrentDimensionalType] = useState(null);
    const [currentDimensionalValue, setCurrentDimensionalValue] = useState(null);
    const [currentDimensionalValueOperation, setCurrentDimensionalValueOperation] = useState(null);

    useEffect(() => {
        if (startDate && endDate) {
            setCurrentObjective({ ...currentObjective, startDate: startDate, endDate: endDate });
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (indicator && isIndicatorDimensional(indicator)) {
            setCurrentDimensionalType(indicator.dimensionalType);
            setCurrentDimensionalValue(null);
        }
        if (dimensionalObjective) {
            setCurrentObjective(dimensionalObjective);
            setSelectedObjectiveType(getDimObjectiveType(dimensionalObjective));
            setOperation('EDIT');
        } else {
            setCurrentObjective(initialDimensionalObjective);
            setSelectedObjectiveType(getDimObjectiveType(initialDimensionalObjective));
            setOperation('CREATE');
        }
    }, [dimensionalObjective, indicator]);

    const handleDimensionalObjectiveTypeChange = (event) => {
        const newDimensionalObjectiveType = event.target.value;
        setSelectedObjectiveType(newDimensionalObjectiveType);
        switch (newDimensionalObjectiveType) {
            case 'Regular-Dimensional': {
                const { startDate, endDate, valuesPerDimension } = currentObjective;
                setCurrentObjective({
                    startDate, endDate, valuesPerDimension,
                    objectiveType: 'QUANTITATIVE_DIMENSIONAL',
                    applicability: null,
                    startComparisonOperator: null,
                    endComparisonOperator: null,
                    joinComparisonAs: null,
                    penalties: null,
                    penaltiesExpressedAs: null,
                });
                break;
            }
            case 'Threshold_Above-Dimensional': {
                const { startDate, endDate, valuesPerDimension, applicability } = currentObjective;
                valuesPerDimension.forEach((value) => {
                    value.endQuantitativeValue = 0;
                });
                setCurrentObjective({
                    startDate, endDate, valuesPerDimension, applicability,
                    objectiveType: 'QUANTITATIVE_DIMENSIONAL_THRESHOLD',
                    startComparisonOperator: 'GREATER_THAN',
                    endComparisonOperator: null,
                    joinComparisonAs: 'AND',
                    penalties: 0.0,
                    penaltiesExpressedAs: 'ABSOLUTE_VALUE',
                });
                break;
            }
            case 'Threshold_Below-Dimensional': {
                const { startDate, endDate, valuesPerDimension, applicability } = currentObjective;
                valuesPerDimension.forEach((value) => {
                    value.endQuantitativeValue = 0;
                });
                setCurrentObjective({
                    startDate, endDate, valuesPerDimension, applicability,
                    objectiveType: 'QUANTITATIVE_DIMENSIONAL_THRESHOLD',
                    startComparisonOperator: 'LESS_THAN',
                    endComparisonOperator: null,
                    joinComparisonAs: 'AND',
                    penalties: 0.0,
                    penaltiesExpressedAs: 'ABSOLUTE_VALUE',
                });
                break;
            }
            case 'Range-Dimensional': {
                const { startDate, endDate, valuesPerDimension, applicability } = currentObjective;
                setCurrentObjective({
                    startDate, endDate, valuesPerDimension, applicability,
                    objectiveType: 'QUANTITATIVE_DIMENSIONAL_THRESHOLD',
                    startComparisonOperator: 'GREATER_THAN',
                    endComparisonOperator: 'LESS_THAN',
                    joinComparisonAs: 'AND',
                    penalties: 0.0,
                    penaltiesExpressedAs: 'ABSOLUTE_VALUE',
                });
                break;
            }
            default:
                break;
        }
    };

    const handleSelectedDimension = (dimensionOrder) => {
        const value = currentObjective.valuesPerDimension.find((dimValue) => dimValue.quantitativeDimensionOrder === dimensionOrder);
        if (value) {
            setCurrentDimensionalValue(value);
            setCurrentDimensionalValueOperation('EDIT');
        } else {
            setCurrentDimensionalValue({ ...initialDimensionalValue, quantitativeDimensionOrder: dimensionOrder });
            setCurrentDimensionalValueOperation('CREATE');
        }
    }

    const handleAddDimension = () => {
        setCurrentObjective({
            ...currentObjective,
            valuesPerDimension: [...currentObjective.valuesPerDimension, currentDimensionalValue]
        });
        setCurrentDimensionalValue(null);
        setCurrentDimensionalValueOperation('EDIT');
    }

    const handleUpdateDimension = () => {
        setCurrentObjective({
            ...currentObjective,
            valuesPerDimension: currentObjective.valuesPerDimension.map((value) => {
                if (value.quantitativeDimensionOrder === currentDimensionalValue.quantitativeDimensionOrder) {
                    return currentDimensionalValue;
                }
                return value;
            })
        });
        setCurrentDimensionalValue(null);
    }

    const handleRemoveDimension = () => {
        setCurrentObjective({
            ...currentObjective,
            valuesPerDimension: currentObjective.valuesPerDimension.filter((value) => value.quantitativeDimensionOrder !== currentDimensionalValue.quantitativeDimensionOrder)
        });
        setCurrentDimensionalValue(null);
        setCurrentDimensionalValueOperation('CREATE');
    }

    const handleCancelDimension = () => {
        setCurrentDimensionalValue(null);
    }

    const handleSave = async () => {
        if (operation === 'CREATE') {
            try {
                const objs = createRegularObjectivesFromDimensionalObjective(currentObjective);
                if (!objs || objs.length === 0) {
                    setError(t('No objectives to save'));
                    showNotification(setNotification, t('Dimensions have not been defined'), 'error', 5000);
                    return;
                }
                await saveNewBulkOfObjectives(loginData.accessToken, planUUID, areaId, areaIndicatorId, objs);
                showNotification(setNotification, t('Dimensional objective has been saved'), 'success', 3000);
            } catch (error) {
                setError(error);
                showNotification(setNotification, t('Error saving dimensional objective'), 'error', 5000);
            }
        } else if (operation === 'EDIT') {
            // I need to compare the currentObjective with the dimensionalObjective to understand what has changed
            // by change we mean if a new dimension has been added, or if a dimension has been removed, or if a dimension has been updated
            // will go through the currentObjective dimensions and compare with the dimensionalObjective dimensions
            const { addedDimensions, removedDimensions, updatedDimensions } = getDimensionChanges(currentObjective, dimensionalObjective);
            if (addedDimensions.length > 0) {
                const rawObjs = [];
                for (const addedDimension of addedDimensions) {
                    const dimensionOrder = addedDimension.quantitativeDimensionOrder;
                    const rawObj = createRegularObjectiveFromDimensionalObjective(currentObjective, dimensionOrder);
                    rawObjs.push(rawObj);
                }
                if (rawObjs.length > 0) {
                    console.log('add rawObjs:', rawObjs);
                    try {
                        await saveNewBulkOfObjectives(loginData.accessToken, planUUID, areaId, areaIndicatorId, rawObjs);
                        showNotification(setNotification, t('Dimensional objective has been updated with new dimensions.'), 'success', 3000);
                    } catch (error) {
                        setError(error);
                        showNotification(setNotification, t('Error updating dimensional objective with new dimensions'), 'error', 5000);
                    }
                }
            }
            if (removedDimensions.length > 0) {
                for (const removedDimension of removedDimensions) {
                    const dimensionOrder = removedDimension.quantitativeDimensionOrder;
                    const rawObj = createRegularObjectiveFromDimensionalObjective(dimensionalObjective, dimensionOrder);
                    console.log('remove rawObj:', rawObj);
                    try {
                        await deleteObjective(loginData.accessToken, planUUID, areaId, areaIndicatorId, rawObj.objectiveId);
                    } catch (error) {
                        setError(error);
                        showNotification(setNotification, t('Error updating dimensional objective with removed dimension'), 'error', 5000);
                    }
                    showNotification(setNotification, t('Dimensional objective has been updated with removed dimensions.'), 'success', 3000);
                }
            }
            if (updatedDimensions.length > 0) {
                for (const updatedDimension of updatedDimensions) {
                    const dimensionOrder = updatedDimension.quantitativeDimensionOrder;
                    const rawObj = createRegularObjectiveFromDimensionalObjective(currentObjective, dimensionOrder);
                    console.log('update rawObj:', rawObj);
                    try {
                        await updateObjective(loginData.accessToken, planUUID, areaId, areaIndicatorId, rawObj);
                    } catch (error) {
                        setError(error);
                        showNotification(setNotification, t('Error updating dimensional objective with updated dimension'), 'error', 5000);
                    }
                    showNotification(setNotification, t('Dimensional objective has been updated with updated dimensions.'), 'success', 3000);
                }
            }
            // On a second stage I will need to compare the dimensionalObjective with the currentObjective to understand what has changed
            // by change we mean if the start date has changed, or if the end date has changed, or if the applicability has changed
            if (hasMainDimObjectiveChanged(currentObjective, dimensionalObjective)) {
                const rawObjs = createRegularObjectivesFromDimensionalObjective(currentObjective);
                for (const rawObj of rawObjs) {
                    try {
                        await updateObjective(loginData.accessToken, planUUID, areaId, areaIndicatorId, rawObj);
                    } catch (error) {
                        setError(error);
                        showNotification(setNotification, t('Error updating dimensional objective'), 'error', 5000);
                    }
                }
            }
        }
        if (onSave) {
            onSave();
        }
    };

    const handleDelete = async () => {
        // console.log('handleDelete');
        if (dimensionalObjective) {
            const rawObjs = createRegularObjectivesFromDimensionalObjective(currentObjective);
            for (const rawObj of rawObjs) {
                try {
                    await deleteObjective(loginData.accessToken, planUUID, areaId, areaIndicatorId, rawObj.objectiveId);
                } catch (error) {
                    setError(error);
                    showNotification(setNotification, t('Error deleting dimensional objective'), 'error', 5000);
                }
            }
        }
        if (onDelete) {
            onDelete();
        }
    }

    const handleCancel = () => {
        // console.log('handleCancel');
        if (onCancel) {
            onCancel();
        }
    };

    const measurementUnit = indicator ? indicator.quantitativeMeasurementUnitDTO.quantitativeMeasurementUnit : '';

    const tabsConfig = [
        {
            label: t('Main dimensional fields'),
            icon: null,
            content: (
                <>
                    <div className='objective-edit-form-obj-main dimensional-objective'>
                        {startDate && endDate && (
                            <DatesRangeEditor
                                datesRangeHolder={currentObjective}
                                setDatesRangeHolder={setCurrentObjective}
                                startDateLabel={t('Start date')}
                                endDateLabel={t('End date')}
                                startDateField='startDate'
                                endDateField='endDate'
                                className={'objective-edit-form-obj-dates-range'}
                                disabledDateRanges={disabledDateRanges}
                            />)}

                        <DimensionalValuesTable
                            dimensionalValues={currentObjective.valuesPerDimension}
                            dimensionalType={currentDimensionalType}
                            objectiveType={selectedObjectiveType}
                            measurementUnit={measurementUnit}
                            onSelectedDimension={handleSelectedDimension}
                        />

                        <div className='dim-objective-edit-form-obj-values'>
                            {selectedObjectiveType === 'Regular-Dimensional' && currentDimensionalValue && (
                                <>
                                    <EditText object={currentDimensionalValue} objectField={'startQuantitativeValue'} setObjectField={setCurrentDimensionalValue} label={t('Start value')} className={'objective-edit-form-obj-value'} suffix={measurementUnit} disabled={currentDimensionalValue ? false : true} />
                                    <EditText object={currentDimensionalValue} objectField={'endQuantitativeValue'} setObjectField={setCurrentDimensionalValue} label={t('End value')} className={'objective-edit-form-obj-value'} suffix={measurementUnit} disabled={currentDimensionalValue ? false : true} />
                                </>
                            )}
                            {((selectedObjectiveType === 'Threshold_Above-Dimensional') || (selectedObjectiveType === 'Threshold_Below-Dimensional')) && currentDimensionalValue && (
                                <EditText object={currentDimensionalValue} objectField={'startQuantitativeValue'} setObjectField={setCurrentDimensionalValue} label={t('Threshold value')} className={'objective-edit-form-obj-value'} suffix={measurementUnit} disabled={currentDimensionalValue ? false : true} />
                            )}
                            {selectedObjectiveType === 'Range-Dimensional' && currentDimensionalValue && (
                                <>
                                    <EditText object={currentDimensionalValue} objectField={'startQuantitativeValue'} setObjectField={setCurrentDimensionalValue} label={t('Lower range value')} className={'objective-edit-form-obj-value'} suffix={measurementUnit} disabled={currentDimensionalValue ? false : true} />
                                    <EditText object={currentDimensionalValue} objectField={'endQuantitativeValue'} setObjectField={setCurrentDimensionalValue} label={t('Higher range value')} className={'objective-edit-form-obj-value'} suffix={measurementUnit} disabled={currentDimensionalValue ? false : true} />
                                </>
                            )}
                        </div>
                        {currentDimensionalValue && (
                            <div className='objective-edit-form-obj-dimensions-actions'>
                                {currentDimensionalValueOperation === 'CREATE' && (
                                    <>
                                        <CustomButton label='' tooltip={t('Add')} icon={<Add />} onClick={handleAddDimension} className={'small-custom-button'} />
                                        <CustomButton label='' tooltip={t('Cancel')} icon={<Cancel />} onClick={handleCancelDimension} className={'small-custom-button'} />
                                    </>
                                )}
                                {currentDimensionalValueOperation === 'EDIT' && (
                                    <>
                                        <CustomButton label='' tooltip={t('Save')} icon={<Save />} onClick={handleUpdateDimension} className={'small-custom-button'} />
                                        <CustomButton label='' tooltip={t('Remove')} icon={<Remove />} onClick={handleRemoveDimension} className={'small-custom-button'} />
                                        <CustomButton label='' tooltip={t('Cancel')} icon={<Cancel />} onClick={handleCancelDimension} className={'small-custom-button'} />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    {((selectedObjectiveType === 'Threshold_Above-Dimensional') || (selectedObjectiveType === 'Threshold_Below-Dimensional') || (selectedObjectiveType === 'Range-Dimensional')) && (
                        <div className='objective-edit-form-obj-values-fails-allowed'>
                            <Tolerance objective={currentObjective} setObjective={setCurrentObjective} edit={true} />
                        </div>
                    )}
                </>
            ),
        },
        {
            label: t('Applicability'),
            icon: null,
            content: (
                <Applicability objective={currentObjective} setObjective={setCurrentObjective} edit={true} />
            ),
            showTab: ((selectedObjectiveType === 'Threshold_Above-Dimensional') || (selectedObjectiveType === 'Threshold_Below-Dimensional') || (selectedObjectiveType === 'Range-Dimensional')),
        },
    ];

    return (
        <B2BFormContainer
            title={(operation == "EDIT") ? 'Edit dimensional objective data' : 'New dimensional objective data'}
            buttonsConfig={[
                { label: t('Save'), onClick: handleSave },
                ...((operation == "EDIT") ? [{
                    label: t('Delete'),
                    confirm: true,
                    onConfirm: handleDelete,
                }] : []),
                { label: t('Cancel'), className: 'b2b-button-cancel white_03', onClick: handleCancel },
            ]}
            className='objective-edit-form multidimensional b2b-form-01'>
            <div className='objective-edit-form-obj-type'>
                {generateSelectField(getDimensionalQuantitativeObjectiveTypes(currentObjective),
                    {
                        label: 'Objective type',
                        onChange: handleDimensionalObjectiveTypeChange,
                        value: selectedObjectiveType,
                        className: 'objective-edit-form-obj-type-select',
                    })}
                <div className='objective-edit-form-obj-image'>
                    {renderSVGImage(selectedObjectiveType)}
                </div>
            </div>
            <div className='objective-edit-form-dim-obj'>
                <GenericTabs tabsConfig={tabsConfig} className={'objective-edit-form-obj'} />
            </div>
        </B2BFormContainer>
    );
};

export default DimQuantitativeObjectiveEditForm;